// @ts-check
// Rotas REST do e-mail: lista de bloqueio (contrato 08 de e-mail avançado), campanhas de e-mail v2, domínio próprio de
// envio e o painel do suporte (contrato 09 de e-mail, seções 10, 13, 14 e 16).
import { api, requestFile } from './api-client.js';

/**
 * @typedef {{ id: string, handle: string, scope: 'MEMBER' | 'WORKSPACE', workspaceMemberId: string | null, createdAt: string }} BlocklistItem
 * @typedef {{ member: BlocklistItem[], workspace: BlocklistItem[], canEditWorkspace: boolean }} Blocklist
 * @typedef {{ total: number, queued: number, sent: number, delivered: number, failed: number, skipped: number, bounced: number, hardBounced: number,
 *   softBounced: number, opened: number, clicked: number, unsubscribed: number, complained: number, duplicates: number }} CampaignCounts
 * @typedef {{ deliveryRate: number | null, openRate: number | null, clickRate: number | null, clickToOpenRate: number | null,
 *   unsubscribeRate: number | null, hardBounceRate: number | null, complaintRate: number | null }} CampaignRates
 * @typedef {'DRAFT' | 'SCHEDULED' | 'PREPARING' | 'SENDING' | 'PAUSED' | 'SENT' | 'SENT_WITH_ERRORS' | 'CANCELED' | 'FAILED'} EmailCampaignStatus
 * @typedef {{ id: string, name: string, formatVersion: 1 | 2, status: EmailCampaignStatus, purpose: string,
 *   audienceDefinition: import('../../../shared/audiences/audience-definition.js').AudienceDefinition | null,
 *   audienceOwnerWorkspaceMemberId: string | null, senderType: 'INSTITUTION' | 'CONNECTED_ACCOUNT', connectedAccountId: string | null,
 *   fromName: string | null, subject: string, preheader: string, editorMode: 'BLOCKS' | 'RICH_TEXT' | 'HTML' | null, document?: any,
 *   hasContent: boolean, compile: { errors: Array<{ code: string, blockId?: string }>, warnings: Array<{ code: string, blockId?: string }> },
 *   sourceTemplateId: string | null, sourceTemplateName: string | null, contentId: string | null, utmEnabled: boolean, mediaCampaignId: string | null,
 *   scheduledAt: string | null, sendPhase: 'WARMUP' | 'WARMUP_HOLD' | 'FULL' | null, warmupHoldUntil: string | null, waitingUntil: string | null,
 *   pauseReason: string | null, pausedAt: string | null, failureReason: string | null, failureDetails: any,
 *   progress: { preparedCards: number, totalCards: number | null }, counts: CampaignCounts, rates: CampaignRates,
 *   sentByWorkspaceMemberId: string | null, startedAt: string | null, sentAt: string | null, lastTestAt: string | null,
 *   contentUpdatedAt: string | null, createdAt: string, updatedAt: string,
 *   legacy: { bodyText: string, audience: { objectNameSingular: string, viewId: string | null, filter: string | null } } | null }} EmailCampaign
 * @typedef {{ code: string, severity: 'ERROR' | 'WARNING' | 'INFO', step?: string, [key: string]: unknown }} ReviewCheck
 * @typedef {{ checks: ReviewCheck[], canSend: boolean, estimate: { cards: number, recipients: number, isEstimate: boolean, days: number } | null }} CampaignReview
 * @typedef {{ id: string, status: string, skipReason: string | null, errorCode: string | null, recipientRole: string | null, sentAt: string | null,
 *   deliveredAt: string | null, firstOpenedAt: string | null, openCount: number, isMachineOpenOnly: boolean, firstClickedAt: string | null,
 *   clickCount: number, bouncedAt: string | null, bounceType: string | null, unsubscribedAt: string | null, complainedAt: string | null,
 *   personId: string | null, recordId: string | null, displayName: string, email: string | null, errorMessage: string | null,
 *   isOutOfScope: boolean }} CampaignRecipient
 * @typedef {{ index: number, label: string, url: string, uniqueClicks: number, totalClicks: number }} CampaignLink
 * @typedef {{ campaign: EmailCampaign, counts: CampaignCounts & { cards: number, recipients: number, skippedByReason: Record<string, number>, humanOpened: number },
 *   rates: CampaignRates, isDeliveryEstimated: boolean, legacyMetrics: boolean, machineOpenShare: number | null,
 *   progress: { done: number, total: number }, events: Array<{ kind: string, occurredAt: string, details: any }>,
 *   topLinks: CampaignLink[], byProvider: Array<{ provider: string, sent: number, delivered: number, opened: number, clicked: number }> }} CampaignReport
 * @typedef {{ kind: string, type: 'CNAME' | 'MX' | 'TXT', name: string, value: string, isRequired: boolean }} DnsRecord
 * @typedef {{ domain: string, senderEmail: string, status: 'PENDING' | 'VERIFIED' | 'FAILED', dkimStatus: string | null, mailFromDomain: string | null,
 *   mailFromStatus: string | null, failureReason: string | null, lastCheckedAt: string | null, verifiedAt: string | null, records: DnsRecord[] }} SendingDomain
 */

/** @param {any} body */
const dataOf = (body) => body?.data ?? {};

// Lista de bloqueio

/** @returns {Promise<Blocklist>} */
export async function getBlocklist() {
  return dataOf(await api.get('/rest/email-blocklist'));
}

/**
 * @param {'MEMBER' | 'WORKSPACE'} scope @param {string} handles e-mails e domínios separados por vírgula
 * @returns {Promise<{ added: BlocklistItem[], errors: Array<{ handle: string, code: 'INVALID_HANDLE' | 'ALREADY_BLOCKED' }> }>}
 */
export async function addToBlocklist(scope, handles) {
  return dataOf(await api.post('/rest/email-blocklist', { scope, handles }));
}

/** @param {string} itemId */
export async function removeFromBlocklist(itemId) {
  await api.delete(`/rest/email-blocklist/${itemId}`);
}

// Campanhas de e-mail v2

/** @param {string} campaignId */
const campaignPath = (campaignId) => `/rest/email-campaigns/${encodeURIComponent(campaignId)}`;

/** @param {{ status?: string, search?: string }} [query] @returns {Promise<EmailCampaign[]>} */
export async function listEmailCampaigns(query = {}) {
  return dataOf(await api.get('/rest/email-campaigns', { status: query.status || undefined, search: query.search || undefined })).campaigns ?? [];
}

/** @param {string} campaignId @returns {Promise<EmailCampaign>} */
export async function getEmailCampaign(campaignId) {
  return dataOf(await api.get(campaignPath(campaignId))).campaign;
}

/** @param {Record<string, unknown>} input @returns {Promise<EmailCampaign>} */
export async function createEmailCampaign(input) {
  return dataOf(await api.post('/rest/email-campaigns', input)).campaign;
}

/** @param {string} campaignId @param {Record<string, unknown>} patch @returns {Promise<EmailCampaign>} */
export async function updateEmailCampaign(campaignId, patch) {
  return dataOf(await api.patch(campaignPath(campaignId), patch)).campaign;
}

/** @param {string} campaignId */
export async function deleteEmailCampaign(campaignId) {
  return dataOf(await api.delete(campaignPath(campaignId))).campaign;
}

/** @param {string} campaignId @param {string} action @param {unknown} [body] */
async function campaignAction(campaignId, action, body = {}) {
  return dataOf(await api.post(`${campaignPath(campaignId)}/${action}`, body));
}

/** @param {string} campaignId @returns {Promise<EmailCampaign>} */
export const duplicateEmailCampaign = async (campaignId) => (await campaignAction(campaignId, 'duplicate')).campaign;
/** @param {string} campaignId @returns {Promise<{ campaign: EmailCampaign, notes: string[], legacy: any }>} */
export const upgradeEmailCampaign = (campaignId) => campaignAction(campaignId, 'upgrade');
/** @param {string} campaignId @param {string} templateId @returns {Promise<EmailCampaign>} */
export const applyTemplateToEmailCampaign = async (campaignId, templateId) => (await campaignAction(campaignId, 'apply-template', { templateId })).campaign;
/** @param {string} campaignId @returns {Promise<CampaignReview>} */
export const reviewEmailCampaign = (campaignId) => campaignAction(campaignId, 'review');
/** @param {string} campaignId @param {{ sampleCardId?: string | null }} [input] */
export const previewEmailCampaign = (campaignId, input = {}) => campaignAction(campaignId, 'preview', input);
/** @param {string} campaignId @param {{ workspaceMemberIds?: string[], sampleCardId?: string | null }} input @returns {Promise<{ sentTo: string[], simulated?: boolean }>} */
export const sendEmailCampaignTest = (campaignId, input) => campaignAction(campaignId, 'test', input);
/** @param {string} campaignId @returns {Promise<EmailCampaign>} */
export const sendEmailCampaign = async (campaignId) => (await campaignAction(campaignId, 'send')).campaign;
/** @param {string} campaignId @param {string} scheduledAt @returns {Promise<EmailCampaign>} */
export const scheduleEmailCampaign = async (campaignId, scheduledAt) => (await campaignAction(campaignId, 'schedule', { scheduledAt })).campaign;
/** @param {string} campaignId @returns {Promise<EmailCampaign>} */
export const unscheduleEmailCampaign = async (campaignId) => (await campaignAction(campaignId, 'unschedule')).campaign;
/** @param {string} campaignId @returns {Promise<EmailCampaign>} */
export const pauseEmailCampaign = async (campaignId) => (await campaignAction(campaignId, 'pause')).campaign;
/** @param {string} campaignId @param {{ acknowledgeRisk?: boolean }} [input] @returns {Promise<EmailCampaign>} */
export const resumeEmailCampaign = async (campaignId, input = {}) => (await campaignAction(campaignId, 'resume', input)).campaign;
/** @param {string} campaignId @returns {Promise<EmailCampaign>} */
export const cancelEmailCampaign = async (campaignId) => (await campaignAction(campaignId, 'cancel')).campaign;
/** @param {string} campaignId @returns {Promise<EmailCampaign>} */
export const backToDraftEmailCampaign = async (campaignId) => (await campaignAction(campaignId, 'back-to-draft')).campaign;

/** @param {string} campaignId @returns {Promise<CampaignReport>} */
export async function getEmailCampaignReport(campaignId) {
  return dataOf(await api.get(`${campaignPath(campaignId)}/report`));
}

/** Conteúdo congelado com os dados de exemplo (aba Conteúdo). @param {string} campaignId */
export async function getEmailCampaignContent(campaignId) {
  return dataOf(await api.get(`${campaignPath(campaignId)}/content`));
}

/**
 * @param {string} campaignId @param {{ status?: string, search?: string, startingAfter?: string | null, limit?: number }} [options]
 * @returns {Promise<{ recipients: CampaignRecipient[], pageInfo: { hasNextPage: boolean, endCursor: string | null } }>}
 */
export async function listEmailCampaignRecipients(campaignId, { status, search, startingAfter, limit = 50 } = {}) {
  return dataOf(await api.get(`${campaignPath(campaignId)}/recipients`, {
    status: status && status !== 'ALL' ? status : undefined, search: search || undefined, starting_after: startingAfter ?? undefined, limit: String(limit),
  }));
}

/** @param {string} campaignId @param {string} [status] */
export async function downloadEmailCampaignRecipientsCsv(campaignId, status) {
  return requestFile(`${campaignPath(campaignId)}/recipients.csv`, { status: status && status !== 'ALL' ? status : undefined });
}

/** @param {string} campaignId @returns {Promise<CampaignLink[]>} */
export async function listEmailCampaignLinks(campaignId) {
  return dataOf(await api.get(`${campaignPath(campaignId)}/links`)).links ?? [];
}

// Domínio próprio de envio (Identidade e envio)

/** @returns {Promise<SendingDomain | null>} */
export async function getSendingDomain() {
  return dataOf(await api.get('/rest/email-settings/domain')).sendingDomain ?? null;
}

/** @param {string} senderEmail @returns {Promise<SendingDomain>} */
export async function setSendingDomain(senderEmail) {
  return dataOf(await api.put('/rest/email-settings/domain', { senderEmail })).sendingDomain;
}

/** @returns {Promise<SendingDomain | null>} */
export async function verifySendingDomain() {
  return dataOf(await api.post('/rest/email-settings/domain/verify', {})).sendingDomain ?? null;
}

export async function removeSendingDomain() {
  return dataOf(await api.delete('/rest/email-settings/domain'));
}

// Painel do suporte (/admin): "Envio de e-mails"

// o painel do suporte fica fora da sessão da escola: sem sessão, a tela de entrada do /admin cuida (sem desviar para o login)
const ADMIN_OPTIONS = Object.freeze({ skipUnauthorizedRedirect: true });

/** @param {number} [days] */
export async function getEmailSendingOverview(days = 7) {
  return dataOf(await api.get('/admin/api/email-sending', { days: String(days) }, ADMIN_OPTIONS));
}

/** @param {string} tenantId @param {{ dailyLimit?: number | null, liftSuspension?: boolean, suspend?: { reason: 'MANUAL' } }} patch */
export async function updateEmailSendingTenant(tenantId, patch) {
  return dataOf(await api.patch(`/admin/api/email-sending/tenants/${encodeURIComponent(tenantId)}`, patch)).policy;
}

/** @param {boolean} paused */
export async function setCampaignSendingSwitch(paused) {
  return dataOf(await api.post('/admin/api/campaign-sending', { paused }, undefined, ADMIN_OPTIONS)).sendingSwitch;
}
